import { ArrowRight, type LucideIcon } from 'lucide-react';
import { cloneElement, isValidElement, type ReactElement, type ReactNode } from 'react';

import { Button } from '~/components/ui/button';
import { cn } from '~/utils/cn';

interface Props {
  /** The destination as a `<Link>` element; its text becomes the label. Omit it and pass `onClick`
   *  + `label` for an in-page action (e.g. a review fallback that opens a panel). */
  children?: ReactElement;
  onClick?: () => void;
  label?: string;
  /** Optional leading icon that says what the list holds (review, images, failed…). */
  icon?: LucideIcon;
  /** `primary` for the one action a stage most wants (e.g. Review products). */
  tone?: 'default' | 'primary';
}

/** Drops a trailing " ›" / " ↗" baked into older labels — the arrow icon replaces it. */
function withoutChevron(node: ReactNode): ReactNode {
  return typeof node === 'string' ? node.replace(/\s*[›↗]\s*$/, '') : node;
}

/**
 * The stage pane's drill-down button — one look everywhere: a tinted outline pill with an optional
 * leading icon and a trailing arrow that nudges on hover, so "go to the filtered list" reads as
 * navigation rather than a form action. Wraps the Link via `Button asChild`, so the anchor itself
 * carries the styles (keyboard, middle-click and open-in-new-tab keep working).
 */
export function PaneActionButton({
  children,
  onClick,
  label: plainLabel,
  icon: Icon,
  tone = 'default',
}: Props) {
  const className = cn(
    'group h-8 w-fit cursor-pointer gap-1.5 rounded-full px-3 text-xs font-medium shadow-none transition-colors',
    tone === 'primary'
      ? 'border-primary/40 bg-primary/5 text-primary hover:border-primary/60 hover:bg-primary/10 hover:text-primary'
      : 'text-foreground hover:border-primary/40 hover:bg-primary/5 hover:text-primary',
  );
  const content = (label: ReactNode) => (
    <>
      {Icon ? <Icon aria-hidden="true" className="size-3.5 shrink-0" /> : null}
      <span>{label}</span>
      <ArrowRight
        aria-hidden="true"
        className="size-3.5 shrink-0 transition-transform group-hover:translate-x-0.5 motion-reduce:transition-none"
      />
    </>
  );

  if (onClick) {
    return (
      <Button variant="outline" size="sm" className={className} onClick={onClick}>
        {content(plainLabel)}
      </Button>
    );
  }
  if (!isValidElement(children)) return null;
  const linkLabel = withoutChevron((children.props as { children?: ReactNode }).children);
  return (
    <Button asChild variant="outline" size="sm" className={className}>
      {cloneElement(children, undefined, content(linkLabel))}
    </Button>
  );
}
